import { call, put, takeLatest } from '@redux-saga/core/effects';
import { fetchWidgetAsync, fetchWidgetRefreshAsync } from '@store/actions/widgets.actions';
import { axiosApi } from '@utils/fetch';
import { Account, Alert, ApiError, Identifier, Statements, Widgets } from '@types';
import { setNotification } from '@store/errors';
import { cmsNameToID, CodingConstants } from '@constants';
import { fetchAccountAsync } from '@store/actions/accounts.action';
import { fetchAlertAsync } from '@store/actions/alerts.actions';
import { all, take, takeEvery } from 'redux-saga/effects';
import { fetchStatementAsync } from '@store/actions/statements.action';
import { getCmsData } from '@services/CmsService';
import { fetchCmsContentAsync } from '@store/actions/cmsContent.actions';
import { nameToIDMatch } from '@utils/name_to_Id_match';
import { fetchPaperlessAsync } from '@store/actions/paperless.action';
import { fetchPayHistoryAsync } from '@store/actions/payHistory.action';
import { fetchAutoPayStatus } from '@store/actions/autoPayStatus.action';
import { fetchConfig } from '@store/actions/config.actions';
import { race } from 'redux-saga-test-plan/matchers';
import { fetchConfig as callConfig } from 'fmc-digital-common';
import { v4 } from 'uuid';
import { config } from '../../config';
import { fetchLIPTileAsync } from '@store/actions/lipTile.action';

export function* fetchWidgetWorker(action: ReturnType<typeof fetchWidgetAsync.request>) {
    const { response, error } = yield call(axiosApi, action.payload);
    if (response) {
        const { redirect, profile, cards } = response;
        if (redirect && (redirect.appName === CodingConstants.customerPortal || redirect.appName === CodingConstants.customerPortal2)) {
            const userLoanType = profile?.identifiers.find((ele: Identifier) => ele.type === CodingConstants.servicingLoan);
            const hasPaperLess = cards?.find((ele: { id: string }) => ele.id === CodingConstants.paperLess);
            if (userLoanType) {
                yield all([put(fetchAccountAsync.request()), put(fetchWidgetAsync.success(response as Widgets))]);
            } else {
                yield put(fetchWidgetAsync.success(response as Widgets));
            }
            if (hasPaperLess) {
                yield put(fetchCmsContentAsync.request(cmsNameToID.paperless));
            }
        } else {
            yield put(fetchWidgetAsync.success(response as Widgets));
        }
    } else if (error) {
        yield put(setNotification(error));
        yield put(fetchWidgetAsync.failure(error as ApiError));
    }
}

export function* fetchLIPTileWorker(action: ReturnType<typeof fetchLIPTileAsync.request>) {
    const { response, error } = yield call(axiosApi, action.payload);
    if (response) {
        yield put(fetchLIPTileAsync.success(response as any));
    } else if (error) {
        yield put(setNotification(error));
        yield put(fetchLIPTileAsync.failure(error as ApiError));
    }
}

export function* fetchAccountWorker(action: ReturnType<typeof fetchAccountAsync.request>) {
    const { response, error } = yield call(axiosApi, action.payload);
    if (response) {
        if (response.servicingLoans.length > 0) {
            yield put(fetchAccountAsync.success(response as Account));
        } else {
            yield put(setNotification(error));
            yield put(fetchAccountAsync.failure(error as ApiError));
        }
    } else if (error) {
        yield put(setNotification(error));
        yield put(fetchAccountAsync.failure(error as ApiError));
    }
}
export function* fetchAlertWorker(action: ReturnType<typeof fetchAlertAsync.request>) {
    const { response } = yield call(axiosApi, action.payload);
    if (response) {
        yield put(fetchAlertAsync.success(response as Alert));
    }
}

export function* fetchWidgetRefreshWorker(action: ReturnType<typeof fetchWidgetRefreshAsync.request>) {
    const { response, error } = yield call(axiosApi, action.payload);
    if (response) {
        const newResponse = { ...response, hasRefreshed: true };
        yield put(fetchWidgetAsync.success(newResponse as Widgets));
    }
    if (error) {
        yield put(fetchWidgetAsync.failure(error as ApiError));
    }
}

export function* fetchCmsContentWorker(action: ReturnType<typeof fetchCmsContentAsync.request>) {
    const name = nameToIDMatch(action.payload.url, cmsNameToID);
    const { response, error } = yield call(getCmsData, action.payload);

    if (response) {
        yield put(fetchCmsContentAsync.success({ [name]: response }));
    }

    if (error) {
        yield put(fetchCmsContentAsync.failure({ [name]: null } as any));
    }
}

export function* fetchPaperlessWorker(action: ReturnType<typeof fetchPaperlessAsync.request>) {
    const { response, error } = yield call(axiosApi, action.payload);

    if (response) {
        yield put(fetchPaperlessAsync.success({ success: true, ...response } as any));
    }

    if (error) {
        yield put(fetchPaperlessAsync.failure({ hasError: true, ...error } as any));
    }
}

export function* fetchPayHistoryWorker(action: ReturnType<typeof fetchPayHistoryAsync.request>) {
    const { response, error } = yield call(axiosApi, action.payload);
    if (response) {
        yield put(fetchPayHistoryAsync.success(response as any));
    }
    if (error) {
        yield put(fetchPayHistoryAsync.failure(error as ApiError));
    }
}

export function* fetchAutoPayStatusWorker(action: ReturnType<typeof fetchAutoPayStatus.request>) {
    const { response, error } = yield call(axiosApi, action.payload);
    if (response) {
        yield put(fetchAutoPayStatus.success(response as any));
    }
    if (error) {
        yield put(fetchAutoPayStatus.failure(error as ApiError));
    }
}

export function* fetchConfigWorker(action: ReturnType<typeof fetchConfig.request>) {
    const { payload } = action;
    payload.headers = {
        'x-correlation-id': v4().toString(),
        'x-api-key': `${config.env('xApiKey')}`
    };
    try {
        const { response } = yield race({
            response: call(callConfig, payload),
            cancel: take(fetchConfig.cancel),
            failed: take(fetchConfig.failure)
        });
        yield put(fetchConfig.success(response));
        yield put(fetchWidgetAsync.request());
    } catch (e) {
        yield put(fetchConfig.failure());
    }
}

export default function* root() {
    yield takeLatest(fetchWidgetAsync.request, fetchWidgetWorker);
    yield takeEvery(fetchLIPTileAsync.request, fetchLIPTileWorker);
    yield takeLatest(fetchAccountAsync.request, fetchAccountWorker);
    yield takeLatest(fetchAlertAsync.request, fetchAlertWorker);
    yield takeEvery(fetchCmsContentAsync.request, fetchCmsContentWorker);
    yield takeEvery(fetchPaperlessAsync.request, fetchPaperlessWorker);
    yield takeEvery(fetchWidgetRefreshAsync.request, fetchWidgetRefreshWorker);
    yield takeEvery(fetchPayHistoryAsync.request, fetchPayHistoryWorker);
    yield takeEvery(fetchAutoPayStatus.request, fetchAutoPayStatusWorker);
    // yield takeEvery(fetchConfig.request, fetchConfigWorker);
}
